/**
 * Model type create / edit form.
 *
 * Layout: two columns on md+, single column on small screens.
 *   Left (md=8): Identity → Parameters cards, then submit row.
 *   Right (md=4): sticky live preview.
 *
 * Parameters are picked via a grouped checkbox grid (`CheckboxGroup`)
 * kept local to this file — it's not yet reused elsewhere, but the
 * sibling `ModelTypeCheckboxGroup` is the mirror that lives in the
 * shared modeltype module and is used by other forms.
 */
import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router';
import * as Yup from 'yup';

import { createModelType, editModelType } from 'src/api/mutations';
import { getModelTypeDetail, getModelTypeList, getParameterList } from 'src/api/queries';
import { LoadingIndicator } from 'src/components/common';
import GenericError from 'src/components/GenericError';

import { FormCard, PreviewCard, PreviewRow } from '../forms';
import PermissionGate from '../../permission-management/PermissionGate';

// Checkbox group for the Parameters multi-select. Kept here because it
// reads a query-shaped prop and isn't generic enough yet for the shared
// forms module.
const CheckboxGroup = ({ disabled, checked, setChecked, error, setErrors, setFieldValue, query }) => {
  const [parameterSet, setParameterSet] = useState(new Set(checked));

  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError) {
    console.error(query.error);
    setErrors({ parameters: 'Could not retrieve parameter list.' });
    return <GenericError error={query.error} />;
  }

  const toggleCheckboxValue = (item) => {
    const newSet = new Set(parameterSet);
    if (newSet.has(item.id)) newSet.delete(item.id);
    else newSet.add(item.id);
    setParameterSet(newSet);
    const newCheckedList = Array.from(newSet);
    setChecked(newCheckedList);
    setFieldValue('parameters', newCheckedList);
  };

  return (
    <FormControl error={Boolean(error)} sx={{ width: '100%' }}>
      <FormGroup>
        <Grid container spacing={1}>
          {query.data.results.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={`parameter-checkbox-${item.id}`}>
              <FormControlLabel
                sx={{ width: '100%', m: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                control={
                  <Checkbox
                    name={item.name}
                    checked={parameterSet.has(item.id)}
                    onChange={() => toggleCheckboxValue(item)}
                    color="primary"
                    disabled={disabled}
                  />
                }
                label={item.name}
              />
            </Grid>
          ))}
        </Grid>
      </FormGroup>
      <FormHelperText>{error}</FormHelperText>
    </FormControl>
  );
};

const validationSchema = Yup.object()
  .shape({
    id: Yup.number().min(1),
    name: Yup.string().max(255).required('Name field is required.'),
    visual_name: Yup.string().max(255).required('Actual Name field is required.'),
    prefix: Yup.string().max(32).required('Prefix is required.'),
    receipt_header_size: Yup.number().min(1).nullable(),
    parameters: Yup.array().required('Picking Parameters is required.'),
  })
  .required();

const ModelTypeForm = (props) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const formAction = props.formAction ?? 'create';
  const isEdit = formAction === 'edit';
  const modelTypeId = isEdit ? props?.initialValues.id : null;
  const [checkedParameters, setCheckedParameters] = useState(
    props?.initialValues?.parameters?.map((parameter) => parameter.id) ?? [],
  );

  const getParameterListQuery = useQuery({
    queryKey: [getParameterList.key],
    queryFn: () => getParameterList.func('page_size=300')
  });

  const createMutationHook = useMutation({
    mutationFn: (formData) => createModelType.func(formData),
    mutationKey: [createModelType.key],

    onSuccess: () => queryClient.invalidateQueries({
      queryKey: [getModelTypeList.key]
    })
  });
  const editMutationHook = useMutation({
    mutationFn: (formData) => editModelType.func(modelTypeId, formData),
    mutationKey: [editModelType.key],

    onSuccess: () => queryClient.invalidateQueries({
      queryKey: [getModelTypeDetail.key, modelTypeId]
    })
  });
  const mutation = isEdit ? editMutationHook : createMutationHook;

  const initialValues = {
    name: props?.initialValues?.name ?? '',
    visual_name: props?.initialValues?.visual_name ?? '',
    prefix: props?.initialValues?.prefix ?? '',
    receipt_header_size: props?.initialValues?.receipt_header_size ?? '',
    parameters: checkedParameters,
  };

  const onSubmit = async (values, { setErrors, setStatus, setSubmitting }) => {
    const data = {
      ...values,
      // Backend defaults receipt_header_size to 0 when empty.
      receipt_header_size: values.receipt_header_size || 0,
    };

    mutation.mutate(data, {
      onSuccess: () => {
        setStatus({ success: true });
        setSubmitting(false);
        enqueueSnackbar(
          isEdit ? 'Model type updated successfully.' : 'Model type created successfully.',
          { anchorOrigin: { horizontal: 'right', vertical: 'top' }, variant: 'success' },
        );
        if (isEdit) navigate(`/dashboard/modeltypes/${modelTypeId}`);
        else navigate('/dashboard/modeltypes');
      },
      onError: (error) => {
        console.error(error);
        setStatus({ success: false });
        const msg = error?.response?.data?.errors?.[0]?.message
          ? Object.values(error.response.data.errors[0].message)
          : ['Submission failed.'];
        setErrors({ submit: msg });
        setSubmitting(false);
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setErrors,
        setFieldValue,
        touched,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                <FormCard title="Identity" description="Internal name, display name and the prefix used in fiscal IDs.">
                  <TextField
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    label="Name"
                    name="name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    variant="outlined"
                    placeholder="RO400"
                    required
                    size="small"
                  />
                  <TextField
                    error={Boolean(touched.visual_name && errors.visual_name)}
                    fullWidth
                    helperText={touched.visual_name && errors.visual_name}
                    label="Visual Name"
                    name="visual_name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.visual_name}
                    variant="outlined"
                    placeholder="RO400 Android POS Device"
                    required
                    size="small"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      error={Boolean(touched.prefix && errors.prefix)}
                      fullWidth
                      helperText={touched.prefix && errors.prefix}
                      label="Model Prefix"
                      name="prefix"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.prefix}
                      variant="outlined"
                      placeholder="AX"
                      required
                      size="small"
                    />
                    <TextField
                      error={Boolean(touched.receipt_header_size && errors.receipt_header_size)}
                      fullWidth
                      helperText={
                        (touched.receipt_header_size && errors.receipt_header_size) ||
                        'Number of header lines printed on a receipt — defaults to 0.'
                      }
                      label="Receipt Header Size"
                      name="receipt_header_size"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.receipt_header_size}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </FormCard>

                <PermissionGate permissions={'commonlib.view_parameter'}>
                  <FormCard title="Parameters" description="Pick the parameters that should be available on this model type.">
                    <CheckboxGroup
                      checked={values.parameters}
                      setChecked={setCheckedParameters}
                      error={errors.parameters}
                      setErrors={setErrors}
                      setFieldValue={setFieldValue}
                      query={getParameterListQuery}
                    />
                  </FormCard>
                </PermissionGate>

                {errors.submit && <FormHelperText error>{errors.submit}</FormHelperText>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    color="primary"
                    component={RouterLink}
                    to={isEdit ? `/dashboard/modeltypes/${modelTypeId}` : `/dashboard/modeltypes`}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    {isEdit ? 'Save' : 'Create'}
                  </Button>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: 16 }}>
                <ModelTypePreviewCard values={values} isEdit={isEdit} />
              </Box>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

const ModelTypePreviewCard = ({ values, isEdit }) => (
  <PreviewCard>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            width: 56,
            height: 56,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
          }}
        >
          <MemoryIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              color: values.visual_name || values.name ? 'text.primary' : 'text.disabled',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {values.visual_name || values.name || 'Model name'}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {values.name && values.visual_name && values.name !== values.visual_name && (
              <Chip
                size="small"
                label={values.name}
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              />
            )}
            {values.prefix && (
              <Chip
                size="small"
                label={`${values.prefix}*`}
                color="primary"
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              />
            )}
          </Stack>
        </Box>
      </Stack>
      <Stack spacing={0.75} sx={{ mt: 2 }}>
        {values.parameters?.length > 0 && (
          <PreviewRow label="Params" value={`${values.parameters.length} selected`} />
        )}
        {(values.receipt_header_size || values.receipt_header_size === 0) && (
          <PreviewRow label="Receipt" value={`${values.receipt_header_size} lines`} />
        )}
      </Stack>
      {!values.name && !values.visual_name && !values.prefix && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
        >
          Fill the form to see the model type take shape.
        </Typography>
      )}
      {isEdit && (
        <Chip size="small" label="Editing" color="info" variant="outlined" sx={{ mt: 2 }} />
      )}
  </PreviewCard>
);

ModelTypeForm.propTypes = {
  formAction: PropTypes.oneOf(['create', 'edit']),
  initialValues: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    visual_name: PropTypes.string,
    prefix: PropTypes.string,
    receipt_header_size: PropTypes.number,
    parameters: PropTypes.array,
  }),
};

export default ModelTypeForm;
